import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { createAccount, fetchAccounts, fetchAccountSummary } from '../../../redux/slices/accountsSlice';
import { Account, PdfTemplate } from '../../../types';
import PhoneInput from '../../../components/common/PhoneInput';
import { isValidEmail, isValidPhoneNumber } from '../../../utils/validation';
import { apiService } from '../../../services/api';

interface AddAccountFormProps {
  open: boolean;
  onClose: () => void;
}

const AddAccountForm: React.FC<AddAccountFormProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  
  const [formData, setFormData] = useState<Partial<Account>>({
    contactID: '',
    hsID: '',
    name: '',
    balance: 0,
    creditLimit: 5000,
    availableCredit: 5000,
    paymentTerm: 30,
    accountStatus: 'Active',
    contactName: '',
    contactNumber: '',
    contactEmail: '',
    invoices: [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [agreementFile, setAgreementFile] = useState<File | null>(null);
  const [requestAgreement, setRequestAgreement] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [creditControlTemplates, setCreditControlTemplates] = useState<PdfTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Email template state
  const [emailTemplateStatus, setEmailTemplateStatus] = useState<{
    hasTemplate: boolean;
    templateName?: string;
    mappableFields: string[];
    loading: boolean;
  }>({
    hasTemplate: false,
    mappableFields: [],
    loading: false,
  });
  
  const [emailFieldMappings, setEmailFieldMappings] = useState<Record<string, string>>({});
  
  // PDF template field mappings state
  const [pdfFieldMappings, setPdfFieldMappings] = useState<Record<string, string>>({});
  const [selectedPdfTemplate, setSelectedPdfTemplate] = useState<PdfTemplate | null>(null);

  // Load credit control templates when request agreement is enabled
  useEffect(() => {
    const loadCreditControlTemplates = async () => {
      if (requestAgreement) {
        setIsLoadingTemplates(true);
        try {
          const templates = await apiService.getPdfTemplatesByType('credit_control');
          setCreditControlTemplates(templates);
        } catch (error) {
          console.error('Failed to load credit control templates:', error);
          setCreditControlTemplates([]);
        } finally {
          setIsLoadingTemplates(false);
        }
      } else {
        setCreditControlTemplates([]);
        setSelectedTemplate('');
      }
    };

    loadCreditControlTemplates();
  }, [requestAgreement]);

  // Check email template status when request agreement is enabled
  useEffect(() => {
    const checkEmailTemplateStatus = async () => {
      if (requestAgreement) {
        setEmailTemplateStatus(prev => ({ ...prev, loading: true }));
        try {
          const status = await apiService.getEmailTemplateTagStatus('Credit Control Agreement Request');
          if (status.hasTemplate) {
            // Get the email template to fetch mappable fields
            const templates = await apiService.getEmailTemplates();
            const emailTemplate = templates.find(t => t.templateId === status.templateId);
            
            if (emailTemplate) {
              // Initialize field mappings with defaults
              const initialMappings: Record<string, string> = {};
              const currentDate = new Date();
              const formattedDate = `${currentDate.getDate().toString().padStart(2, '0')}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getFullYear()}`;
              
              emailTemplate.mappableFields.forEach((field: string) => {
                if (field === 'ApprovedName' && user) {
                  initialMappings[field] = `${user.firstName} ${user.lastName}`;
                } else if (field === 'CreditLimit') {
                  initialMappings[field] = 'Credit Limit';
                } else if (field === 'DateTime') {
                  initialMappings[field] = formattedDate;
                } else {
                  initialMappings[field] = '';
                }
              });
              
              setEmailFieldMappings(initialMappings);
              setEmailTemplateStatus({
                hasTemplate: true,
                templateName: status.templateName,
                mappableFields: emailTemplate.mappableFields,
                loading: false,
              });
            }
          } else {
            setEmailTemplateStatus({
              hasTemplate: false,
              mappableFields: [],
              loading: false,
            });
          }
        } catch (error) {
          console.error('Error checking email template status:', error);
          setEmailTemplateStatus({
            hasTemplate: false,
            mappableFields: [],
            loading: false,
          });
        }
      } else {
        setEmailTemplateStatus({
          hasTemplate: false,
          mappableFields: [],
          loading: false,
        });
        setEmailFieldMappings({});
      }
    };

    checkEmailTemplateStatus();
  }, [requestAgreement, user]);

  // Fetch PDF template mappable fields when template is selected
  useEffect(() => {
    const fetchPdfTemplateFields = async () => {
      if (selectedTemplate && creditControlTemplates.length > 0) {
        const template = creditControlTemplates.find(t => t.templateId === selectedTemplate);
        if (template) {
          setSelectedPdfTemplate(template);
          
          // Initialize PDF field mappings with defaults if template has extractedFields
          if (template.extractedFields && template.extractedFields.length > 0) {
            const initialMappings: Record<string, string> = {};
            const currentDate = new Date();
            const formattedDate = `${currentDate.getDate().toString().padStart(2, '0')}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getFullYear()}`;
            
            template.extractedFields.forEach((field: string) => {
              if (field === 'ApprovedName' && user) {
                initialMappings[field] = `${user.firstName} ${user.lastName}`;
              } else if (field === 'CreditLimit') {
                initialMappings[field] = 'Credit Limit';
              } else if (field === 'DateTime') {
                initialMappings[field] = formattedDate;
              } else if (field === 'termDays') {
                initialMappings[field] = 'Payment Term';
              } else {
                initialMappings[field] = '';
              }
            });
            
            setPdfFieldMappings(initialMappings);
          }
        }
      } else {
        setSelectedPdfTemplate(null);
        setPdfFieldMappings({});
      }
    };

    fetchPdfTemplateFields();
  }, [selectedTemplate, creditControlTemplates, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Handle numeric fields
    if (['balance', 'creditLimit', 'availableCredit', 'paymentTerm'].includes(name)) {
      setFormData({
        ...formData,
        [name]: parseFloat(value) || 0,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name) newErrors.name = 'Company name is required';
    if (!formData.contactID) newErrors.contactID = 'Xero ID is required';
    if (!formData.hsID) newErrors.hsID = 'HubSpot ID is required';
    if (!formData.contactName) newErrors.contactName = 'Contact name is required';
    if (!formData.contactNumber) newErrors.contactNumber = 'Contact number is required';
    if (formData.contactNumber && !isValidPhoneNumber(formData.contactNumber)) {
      newErrors.contactNumber = 'Valid phone number is required';
    }
    if (!formData.contactEmail) newErrors.contactEmail = 'Contact email is required';
    if (formData.contactEmail && !isValidEmail(formData.contactEmail)) {
      newErrors.contactEmail = 'Valid email is required';
    }
    
    // Credit Agreement validation - either file uploaded or request agreement checked
    if (!agreementFile && !requestAgreement) {
      newErrors.agreement = 'Either upload an agreement or select "Request Agreement"';
    }
    
    // If request agreement is selected, template must be selected
    if (requestAgreement && !selectedTemplate) {
      newErrors.template = 'Please select a template for the agreement';
    }
    
    // If PDF template has extractedFields, validate field mappings
    if (requestAgreement && selectedPdfTemplate && selectedPdfTemplate.extractedFields && selectedPdfTemplate.extractedFields.length > 0) {
      selectedPdfTemplate.extractedFields.forEach((field: string) => {
        if (!pdfFieldMappings[field]) {
          newErrors[`pdfField_${field}`] = `${field} mapping is required`;
        }
      });
    }
    
    // If email template is available and request agreement is checked, validate field mappings
    if (requestAgreement && emailTemplateStatus.hasTemplate && emailTemplateStatus.mappableFields.length > 0) {
      emailTemplateStatus.mappableFields.forEach((field: string) => {
        if (!emailFieldMappings[field]) {
          newErrors[`emailField_${field}`] = `${field} mapping is required`;
        }
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePhoneChange = (value: string) => {
    setFormData({
      ...formData,
      contactNumber: value
    });
  };

  const handleTemplateChange = (event: SelectChangeEvent<string>) => {
    setSelectedTemplate(event.target.value);
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setIsUploading(true);
      
      try {
        // Set balance to 0 and calculate available credit
        const balance = 0;
        const availableCredit = (formData.creditLimit || 0) - balance;
        
        const accountData = {
          ...formData,
          balance,
          availableCredit,
        } as Account;
        
        // Create the account first
        await dispatch(createAccount(accountData)).unwrap();
        
        // If there's an agreement file, upload it
        if (agreementFile && formData.contactID) {
          try {
            await apiService.uploadAgreement(formData.contactID, agreementFile);
            console.log('Agreement uploaded successfully');
          } catch (uploadError) {
            console.error('Failed to upload agreement:', uploadError);
            // Don't fail the whole process if agreement upload fails
            alert('Account created successfully, but failed to upload agreement. You can upload it later from the edit dialog.');
          }
        }
        
        // If request agreement is selected, handle PDF generation and email sending
        if (requestAgreement) {
          let pdfFile: File | null = null;
          
          // Try to generate PDF if we have a selected template
          if (selectedTemplate) {
            try {
              // Get current user info for firstName and lastName
              const currentUser = await apiService.getCurrentUser();
              if (!currentUser) {
                throw new Error('Unable to get current user information');
              }

              // Create filename: companyname+hubspotid with special chars removed
              const sanitizedCompanyName = (formData.name || '').replace(/[^a-zA-Z0-9]/g, '');
              const sanitizedHsId = (formData.hsID || '').replace(/[^a-zA-Z0-9]/g, '');
              const filename = `${sanitizedCompanyName}${sanitizedHsId}`;

              // Prepare field mappings from user configuration
              const fieldMappings: Record<string, string> = {};
              
              // Map the PDF field mappings to actual values
              Object.entries(pdfFieldMappings).forEach(([field, mappingValue]) => {
                let actualValue = '';
                
                switch (mappingValue) {
                  case 'Company Name':
                    actualValue = formData.name || '';
                    break;
                  case 'Xero ID':
                    actualValue = formData.contactID || '';
                    break;
                  case 'HubSpot ID':
                    actualValue = formData.hsID || '';
                    break;
                  case 'Credit Limit':
                    actualValue = formData.creditLimit === 99999999 ? 'Unlimited' : (formData.creditLimit || 0).toString();
                    break;
                  case 'Payment Term':
                    actualValue = (formData.paymentTerm || 30).toString();
                    break;
                  case 'Contact Name':
                    actualValue = formData.contactName || '';
                    break;
                  case 'Contact Number':
                    actualValue = formData.contactNumber || '';
                    break;
                  case 'Contact Email':
                    actualValue = formData.contactEmail || '';
                    break;
                  case `${currentUser.firstName} ${currentUser.lastName}`:
                    actualValue = `${currentUser.firstName} ${currentUser.lastName}`;
                    break;
                  default:
                    // Check if it's a date format
                    if (mappingValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
                      actualValue = mappingValue;
                    } else {
                      actualValue = mappingValue;
                    }
                    break;
                }
                
                fieldMappings[field] = actualValue;
              });

              console.log('Generating PDF with mappings:', fieldMappings);

              // Generate PDF
              const pdfBlob = await apiService.generatePdf({
                templateId: selectedTemplate,
                fieldMappings,
                filename
              });

              // Convert blob to file for upload
              pdfFile = new File([pdfBlob], `${filename}.pdf`, { type: 'application/pdf' });

              // Upload the generated PDF as the agreement
              if (formData.contactID) {
                await apiService.uploadAgreement(formData.contactID, pdfFile);
                console.log('Generated PDF uploaded successfully as agreement');
              }
            } catch (pdfError) {
              console.error('Failed to generate or upload PDF agreement:', pdfError);
              alert('Account created successfully, but failed to generate agreement PDF. You can upload it manually later.');
            }
          }

          // If email template is available, send email (with or without PDF attachment)
          if (emailTemplateStatus.hasTemplate && formData.contactEmail) {
            let emailTemplateStatus_check: any = null;
            try {
              // Get current user info for firstName and lastName
              const currentUser = await apiService.getCurrentUser();
              if (!currentUser) {
                throw new Error('Unable to get current user information');
              }

              // Get the email template ID
              emailTemplateStatus_check = await apiService.getEmailTemplateTagStatus('Credit Control Agreement Request');
              if (emailTemplateStatus_check.hasTemplate && emailTemplateStatus_check.templateId) {
                // Prepare email body elements from the field mappings we configured
                const emailBodyElements: Record<string, string> = {};
                
                // Map the email field mappings to actual values
                Object.entries(emailFieldMappings).forEach(([field, mappingValue]) => {
                  let actualValue = '';
                  
                  switch (mappingValue) {
                    case 'Company Name':
                      actualValue = formData.name || '';
                      break;
                    case 'Xero ID':
                      actualValue = formData.contactID || '';
                      break;
                    case 'HubSpot ID':
                      actualValue = formData.hsID || '';
                      break;
                    case 'Credit Limit':
                      actualValue = formData.creditLimit === 99999999 ? 'Unlimited' : `£${formData.creditLimit || 0}`;
                      break;
                    case 'Payment Term':
                      actualValue = `${formData.paymentTerm || 30} days`;
                      break;
                    case 'Contact Name':
                      actualValue = formData.contactName || '';
                      break;
                    case 'Contact Number':
                      actualValue = formData.contactNumber || '';
                      break;
                    case 'Contact Email':
                      actualValue = formData.contactEmail || '';
                      break;
                    case `${currentUser.firstName} ${currentUser.lastName}`:
                      actualValue = `${currentUser.firstName} ${currentUser.lastName}`;
                      break;
                    default:
                      // Check if it's a date format
                      if (mappingValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
                        actualValue = mappingValue;
                      } else {
                        actualValue = mappingValue;
                      }
                      break;
                  }
                  
                  emailBodyElements[field] = actualValue;
                });

                console.log('Sending email with body elements:', emailBodyElements);

                // Get the email template to get the database ID
                const templates = await apiService.getEmailTemplates();
                const emailTemplate = templates.find(t => t.templateId === emailTemplateStatus_check.templateId);
                
                if (!emailTemplate) {
                  throw new Error('Email template not found');
                }

                // Prepare the email request object for debugging
                const emailRequest = {
                  template: true,
                  templateID: emailTemplate.id, // Use the database ID, not the UUID
                  toEmail: formData.contactEmail,
                  fromEmail: 'noreply@csttraining.co.uk',
                  subject: 'CST Training LTD - Please find your Credit Agreement Attached',
                  bodyElements: emailBodyElements,
                  attachment: pdfFile || undefined
                };

                console.log('Email request object:', emailRequest);

                // Send email with or without PDF attachment
                await apiService.sendEmail(emailRequest);

                console.log('Email sent successfully' + (pdfFile ? ' with PDF attachment' : ' without attachment'));
              }
            } catch (emailError) {
              console.error('Failed to send email:', emailError);
              
              // Create debug info for the error message
              const debugInfo = {
                templateID: emailTemplateStatus_check?.templateId || 'Unknown',
                toEmail: formData.contactEmail,
                fromEmail: 'noreply@csttraining.co.uk',
                subject: 'CST Training LTD - Please find your Credit Agreement Attached',
                bodyElements: emailFieldMappings,
                hasAttachment: !!pdfFile,
                error: emailError instanceof Error ? emailError.message : 'Unknown error'
              };
              
              console.error('Email debug info:', debugInfo);
              alert(`Account created successfully, but failed to send email. Debug info: ${JSON.stringify(debugInfo, null, 2)}`);
            }
          }
        }
        
        // Refresh the accounts list
        dispatch(fetchAccounts());
        dispatch(fetchAccountSummary());
        onClose();
        
      } catch (error) {
        console.error('Failed to create account:', error);
        alert('Failed to create account. Please try again.');
      } finally {
        setIsUploading(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add New Account</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Account Information
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                label="Company Name"
                name="name"
                value={formData.name || ''}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Box>
            
            <Box sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
              <TextField
                select
                fullWidth
                label="Account Status"
                name="accountStatus"
                value={formData.accountStatus || 'Active'}
                onChange={handleChange}
                sx={{ '& .MuiInputBase-root': { height: '56px' } }}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Suspended">Suspended</MenuItem>
                <MenuItem value="Closed">Closed</MenuItem>
              </TextField>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                label="Xero ID"
                name="contactID"
                value={formData.contactID || ''}
                onChange={handleChange}
                error={!!errors.contactID}
                helperText={errors.contactID}
                required
              />
            </Box>
            
            <Box sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                label="HubSpot ID"
                name="hsID"
                value={formData.hsID || ''}
                onChange={handleChange}
                error={!!errors.hsID}
                helperText={errors.hsID}
                required
              />
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                label="Credit Limit"
                name="creditLimit"
                type="number"
                value={formData.creditLimit === 99999999 ? 'Unlimited' : formData.creditLimit || 0}
                onChange={handleChange}
                disabled={formData.creditLimit === 99999999}
                InputProps={{ 
                  startAdornment: '£',
                  readOnly: formData.creditLimit === 99999999
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.creditLimit === 99999999}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          creditLimit: 99999999,
                          availableCredit: 99999999
                        });
                      } else {
                        setFormData({
                          ...formData,
                          creditLimit: 5000,
                          availableCredit: 5000
                        });
                      }
                    }}
                    color="primary"
                  />
                }
                label="No Credit Limit"
                sx={{ mt: 1 }}
              />
            </Box>
            
            <Box sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                label="Payment Term (Days)"
                name="paymentTerm"
                type="number"
                value={formData.paymentTerm || 30}
                onChange={handleChange}
              />
            </Box>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Contact Information
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                label="Contact Name"
                name="contactName"
                value={formData.contactName || ''}
                onChange={handleChange}
                error={!!errors.contactName}
                helperText={errors.contactName}
                required
              />
            </Box>
            
            <Box sx={{ width: { xs: '100%', md: 'calc(50% - 8px)' } }}>
              <PhoneInput
                label="Contact Number"
                value={formData.contactNumber || ''}
                onChange={handlePhoneChange}
                error={!!errors.contactNumber}
                helperText={errors.contactNumber}
                required
                fullWidth
              />
            </Box>
          </Box>
          
          <Box>
            <TextField
              fullWidth
              label="Contact Email"
              name="contactEmail"
              value={formData.contactEmail || ''}
              onChange={handleChange}
              error={!!errors.contactEmail}
              helperText={errors.contactEmail}
              required
            />
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Credit Agreement *
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Either upload a signed agreement or request one to be sent to the client.
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                component="label"
                sx={{ alignSelf: 'flex-start' }}
                disabled={requestAgreement}
              >
                Upload Agreement (PDF)
                <input
                  type="file"
                  accept=".pdf"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.type !== 'application/pdf') {
                        alert('Please select a PDF file');
                        return;
                      }
                      if (file.size > 10 * 1024 * 1024) {
                        alert('File size must be less than 10MB');
                        return;
                      }
                      // Store the file for later upload after account creation
                      setAgreementFile(file);
                      // Clear request agreement if file is uploaded
                      setRequestAgreement(false);
                      setSelectedTemplate('');
                    }
                  }}
                />
              </Button>
              
              {agreementFile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Selected: {agreementFile.name}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setAgreementFile(null)}
                  >
                    Remove
                  </Button>
                </Box>
              )}
              
              <FormControlLabel
                control={
                  <Switch
                    checked={requestAgreement}
                    onChange={(e) => {
                      setRequestAgreement(e.target.checked);
                      // Clear uploaded file if request agreement is checked
                      if (e.target.checked) {
                        setAgreementFile(null);
                      } else {
                        setSelectedTemplate('');
                      }
                    }}
                    color="primary"
                    disabled={!!agreementFile}
                  />
                }
                label="Request Agreement (will be sent to client for signature)"
              />
              
              {/* PDF Template Selection Dropdown */}
              {requestAgreement && (
                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth error={!!errors.template}>
                    <InputLabel id="template-select-label">Select Agreement Template *</InputLabel>
                    <Select
                      labelId="template-select-label"
                      value={selectedTemplate}
                      label="Select Agreement Template *"
                      onChange={handleTemplateChange}
                      disabled={isLoadingTemplates}
                    >
                      {isLoadingTemplates ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading templates...
                        </MenuItem>
                      ) : creditControlTemplates.length === 0 ? (
                        <MenuItem disabled>No credit control templates available</MenuItem>
                      ) : (
                        creditControlTemplates.map((template) => (
                          <MenuItem key={template.templateId} value={template.templateId}>
                            {template.documentName || `Template ${template.templateId}`}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {errors.template && (
                      <Typography variant="body2" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.template}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
              )}

              {/* PDF Template Field Mapping Card */}
              {requestAgreement && selectedPdfTemplate && selectedPdfTemplate.extractedFields && selectedPdfTemplate.extractedFields.length > 0 && (
                <Card sx={{ mt: 2, border: '1px solid', borderColor: 'secondary.main' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="secondary">
                      PDF Template Field Mapping
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Configure the field mappings for the PDF template "{selectedPdfTemplate.documentName}". All fields are required.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {selectedPdfTemplate.extractedFields.map((field) => {
                        const fieldOptions = [
                          'Company Name',
                          'Xero ID', 
                          'HubSpot ID',
                          'Credit Limit',
                          'Payment Term',
                          'Contact Name',
                          'Contact Number',
                          'Contact Email',
                          user ? `${user.firstName} ${user.lastName}` : 'Current User',
                          new Date().toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric' 
                          })
                        ];

                        return (
                          <FormControl key={field} fullWidth>
                            <InputLabel>{field} *</InputLabel>
                            <Select
                              value={pdfFieldMappings[field] || ''}
                              label={`${field} *`}
                              onChange={(e) => setPdfFieldMappings(prev => ({
                                ...prev,
                                [field]: e.target.value
                              }))}
                            >
                              {fieldOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Email Template Field Mapping Card */}
              {requestAgreement && emailTemplateStatus.hasTemplate && emailTemplateStatus.mappableFields.length > 0 && (
                <Card sx={{ mt: 2, border: '1px solid', borderColor: 'primary.main' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Email Template Field Mapping
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Configure the field mappings for the email template "{emailTemplateStatus.templateName}". All fields are required.
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {emailTemplateStatus.mappableFields.map((field) => {
                        const fieldOptions = [
                          'Company Name',
                          'Xero ID', 
                          'HubSpot ID',
                          'Credit Limit',
                          'Payment Term',
                          'Contact Name',
                          'Contact Number',
                          'Contact Email',
                          user ? `${user.firstName} ${user.lastName}` : 'Current User',
                          new Date().toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric' 
                          })
                        ];

                        return (
                          <FormControl key={field} fullWidth>
                            <InputLabel>{field} *</InputLabel>
                            <Select
                              value={emailFieldMappings[field] || ''}
                              label={`${field} *`}
                              onChange={(e) => setEmailFieldMappings(prev => ({
                                ...prev,
                                [field]: e.target.value
                              }))}
                            >
                              {fieldOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
            
            {errors.agreement && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.agreement}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isUploading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={isUploading}
          startIcon={isUploading ? <CircularProgress size={20} /> : (agreementFile ? <CloudUploadIcon /> : undefined)}
        >
          {isUploading ? 'Creating Account...' : 'Create Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAccountForm;
